import { Controller } from "@hotwired/stimulus"
import { moveId, movedLabel, reconcileOrder, nameError } from "../modules/sections_list.mjs"

// Lista de Secciones del menú: orden optimista con flechas, formulario de crear y estado vacío.
// Lo persistido lo pinta el servidor por stream. Doc: _docs/frontend/catalog_presentation/partner_sections.md → Cliente
export default class extends Controller {
  static targets = ["list", "form", "input", "draftHint", "draftSubmit", "addButton", "orderButton", "status", "card"]
  static values = { reorderUrl: String }

  connect() {
    this.savedOrder = this.#ids()
    // Mutación ajena (stream): reconcilia el orden conocido y resalta la fila nueva; propia: solo recuenta
    this.observer = new MutationObserver(() => {
      if (!this.ownMutation) {
        const before = this.savedOrder
        this.savedOrder = reconcileOrder(before, this.#ids(), this.inflight || Boolean(this.pending))
        this.#highlightNew(this.savedOrder.filter(id => !before.includes(id)))
      }
      this.ownMutation = false
      this.#schedule()
    })
    this.observer.observe(this.listTarget, { childList: true })
    // El modo Ordenar es de mobile: al girar a la tabla (--sections-layout) se apaga solo
    this.resizeObserver = new ResizeObserver(() => this.#leaveOrderingInTable())
    this.resizeObserver.observe(this.element)
    this.apply()
  }

  disconnect() {
    this.observer.disconnect()
    this.resizeObserver.disconnect()
    clearTimeout(this.timer)
    clearTimeout(this.movedTimer)
  }

  // Un apply() por tanda de cambios (setTimeout, no rAF: ver doc)
  #schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.apply(), 0)
  }

  // Idempotente, derivado del DOM: vacío, números 1..n y flechas de los bordes
  apply() {
    const rows = this.#rows()
    this.element.classList.toggle("is-empty", rows.length === 0)
    if (rows.length === 0) this.#setOrdering(false)
    rows.forEach((row, index) => {
      this.#setText(row.querySelector("[data-section-row-target='position']"), String(index + 1))
      row.querySelector("[data-section-row-target='up']").disabled = index === 0
      row.querySelector("[data-section-row-target='down']").disabled = index === rows.length - 1
    })
  }

  // ---- Orden ----

  toggleOrdering() {
    this.#setOrdering(!this.element.classList.contains("is-ordering"))
  }

  // section-row:move → mueve, anuncia y guarda
  move(event) {
    const { id, delta } = event.detail
    const ids = this.#ids()
    const next = moveId(ids, id, delta)
    if (next === ids) return
    this.#render(next)
    this.apply() // flechas al día antes de enfocar
    this.#announce(id, next.indexOf(id) + 1, delta)
    this.#save(next)
  }

  #leaveOrderingInTable() {
    if (!this.element.classList.contains("is-ordering")) return
    if (getComputedStyle(this.cardTarget).getPropertyValue("--sections-layout").trim() === "table") this.#setOrdering(false)
  }

  #setOrdering(on) {
    if (on) this.hideForm()
    this.element.classList.toggle("is-ordering", on)
    this.orderButtonTarget.setAttribute("aria-pressed", String(on))
    this.orderButtonTarget.setAttribute("aria-label", on ? "Terminar de ordenar" : "Ordenar secciones")
    if (!on) this.#clearMoved()
  }

  // Reubica los nodos según `ids`; las filas que no nombra quedan al final, las ausentes se ignoran
  #render(ids) {
    const byId = new Map(this.#rows().map(row => [row.dataset.sectionId, row]))
    const order = ids.filter(id => byId.has(id)).concat(this.#ids().filter(id => !ids.includes(id)))
    this.ownMutation = true
    order.forEach(id => this.listTarget.appendChild(byId.get(id)))
  }

  // Resalta la fila movida un momento y deja el foco en la flecha presionada (o en la otra si quedó disabled)
  #announce(id, position, delta) {
    this.#clearMoved()
    const row = this.#rows().find(row => row.dataset.sectionId === id)
    const label = movedLabel(position)
    row.classList.add("is-moved")
    this.#setText(row.querySelector("[data-section-row-target='moved']"), label)
    const pressed = row.querySelector(`[data-section-row-target='${delta < 0 ? "up" : "down"}']`)
    const other = row.querySelector(`[data-section-row-target='${delta < 0 ? "down" : "up"}']`)
    ;(pressed.disabled ? other : pressed).focus()
    this.#setText(this.statusTarget, `${row.dataset.sectionName}: ${label}`)
    clearTimeout(this.movedTimer)
    this.movedTimer = setTimeout(() => this.#clearMoved(), 2500)
  }

  #clearMoved() {
    this.#rows().forEach(row => {
      row.classList.remove("is-moved")
      this.#setText(row.querySelector("[data-section-row-target='moved']"), "")
    })
  }

  // Un envío a la vez con el último orden; si el servidor no confirma, vuelve al último guardado
  async #save(ids) {
    this.pending = ids
    if (this.inflight) return
    this.inflight = true
    try {
      while (this.pending) {
        const order = this.pending
        this.pending = null
        const response = await fetch(this.reorderUrlValue, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken() },
          body: JSON.stringify({ section_ids: order })
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        this.savedOrder = order
      }
    } catch (error) {
      this.pending = null
      console.error("No se pudo guardar el orden de las secciones", error)
      window.toastr?.error("No se pudo guardar el orden. Las secciones volvieron a su lugar.")
      this.#render(this.savedOrder)
      this.#clearMoved()
    } finally {
      this.inflight = false
    }
  }

  // ---- Crear ----

  showForm() {
    this.#setOrdering(false)
    this.formTarget.classList.add("is-open")
    this.inputTarget.focus()
  }

  hideForm() {
    this.formTarget.classList.remove("is-open")
    this.inputTarget.value = ""
    this.#showDraftError(null)
  }

  // Al escribir avisa el repetido; el vacío, al intentar crear (guardCreate)
  validateDraft() {
    this.#showDraftError(this.inputTarget.value.trim() === "" ? null : nameError(this.inputTarget.value, this.#names()))
  }

  guardCreate(event) {
    const error = nameError(this.inputTarget.value, this.#names())
    if (!error) return
    event.preventDefault()
    this.#showDraftError(error)
    this.inputTarget.focus()
  }

  creating() {
    this.submitting = true
  }

  // El servidor reemplaza el form: abierto con aviso (422) → foco al campo; cerrado (éxito) → al botón
  formTargetConnected(form) {
    if (!this.submitting) return
    this.submitting = false
    if (form.classList.contains("is-open")) this.inputTarget.focus()
    else this.addButtonTarget.focus()
  }

  #showDraftError(error) {
    this.draftHintTarget.textContent = error ?? ""
    this.draftHintTarget.hidden = !error
    this.inputTarget.setAttribute("aria-invalid", String(Boolean(error)))
    this.draftSubmitTarget.setAttribute("aria-disabled", String(Boolean(error)))
  }

  #highlightNew(ids) {
    ids.forEach(id => {
      const row = this.#rows().find(row => row.dataset.sectionId === id)
      row.classList.add("is-new")
      row.scrollIntoView({ block: "nearest", behavior: "smooth" })
      setTimeout(() => row.classList.remove("is-new"), 2000)
    })
  }

  // turbo:before-cache: nada abierto ni en modo Ordenar en el snapshot
  reset() {
    this.#setOrdering(false)
    if (!this.element.classList.contains("is-empty")) this.hideForm()
  }

  // ---- DOM ----

  #rows() {
    return Array.from(this.listTarget.querySelectorAll(":scope > [data-section-id]"))
  }

  #ids() {
    return this.#rows().map(row => row.dataset.sectionId)
  }

  #names() {
    return this.#rows().map(row => row.dataset.sectionName)
  }

  // Solo escribe si cambió (el observer no se retroalimenta)
  #setText(element, text) {
    if (element && element.textContent !== text) element.textContent = text
  }
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}
